import { Box, Button, Grid, HStack } from "@chakra-ui/react";
import ModalContractor from "@components/modules/contractors/ModalContractor";
import {
  DocumentsCtx,
  StoreDocuments,
} from "@components/modules/documents/listing/StoreDocuments";
import { TableModuleDocuments } from "@components/modules/documents/listing/TableModuleDocuments";
import { TableModuleExpenses } from "@components/modules/expenses/listing/TableModuleExpenses";
import { Trans, t } from "@lingui/macro";
import { useUpdateContractorMutation } from "@src/__generated__/graphql";
import { Icon } from "@src/components/ui-kit/Icon";
import { can } from "@src/utils/components/permissions";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { formatIBAN } from "@src/utils/formatters";
import { useStore } from "@src/utils/hooks";
import { useScreenType } from "@src/utils/hooks/useIsMobile";
import { observer } from "mobx-react-lite";
import React, {
  type FunctionComponent,
  type PropsWithChildren,
  useState,
} from "react";
import type { Store } from "../../ModalContractor/store";
import { MixedFilters } from "../documents/MixedFilters";
import { Contractor } from "../models";
import { CardAvatar, CardHeader, CardIcon, InfoCardRow } from "./common";

interface DetailProps {
  contractor: Contractor;
  setContractor: (val: Contractor) => void;
}

export const Detail: FunctionComponent<DetailProps> = observer(function Detail({
  contractor,
  setContractor,
}) {
  const { isMobile } = useScreenType();
  const { workspaceStore } = useStore();
  const [documentsStore] = useState(() => {
    const store = new StoreDocuments(new MixedFilters(contractor.id), true);
    store.setPrefillFormFields({ contractorId: contractor.id });
    store.setShowPart(new Set(["contracts"]));
    return store;
  });

  const [editStore] = useState(() => new ModalContractor.Store());

  const [update, { loading: loadingUpdateContractor }] =
    useUpdateContractorMutation({
      onCompleted({ updateContractor }) {
        if (updateContractor) {
          setContractor(new Contractor(updateContractor));
        }
        editStore.toggleOpen(false);
      },
    });

  const onOpenModal = () => {
    editStore.form.reset();
    editStore.form.fill(contractor.serialize());
    editStore.toggleOpen(true);
  };

  const onEditContractor = async () => {
    const { hasError } = await editStore.form.validateForContractor();
    if (hasError) return;
    update({
      variables: {
        input: { id: contractor.id, ...editStore.form.serialize() },
      },
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center"></div>

      <Grid
        templateColumns={{
          base: "1fr",
          md: "0.5fr 0.5fr",
        }}
      >
        <Box
          p="6"
          borderRight={{
            base: "none",
            md: "1px",
          }}
          borderRightColor={{
            base: "transparent",
            md: "gray.200",
          }}
          borderBottom={{
            base: "1px",
            md: "none",
          }}
          borderBottomColor={{
            base: "gray.200",
            md: "transparent",
          }}
        >
          <WrapComponent
            if={can("contract_update") && isMobile}
            with={(children) => (
              <EditModalWrapper
                editStore={editStore}
                loadingUpdateContractor={loadingUpdateContractor}
                onEditContractor={onEditContractor}
                onOpenModal={onOpenModal}
              >
                {children}
              </EditModalWrapper>
            )}
          >
            <CardHeader title={t`Contractor Details`} icon={<CardAvatar />} />
          </WrapComponent>

          <InfoCardRow title={t`Title:`} value={contractor.legal_name} />
          <InfoCardRow title={t`Street:`} value={contractor.street} />
          <InfoCardRow title={t`City:`} value={contractor.city} />
          <InfoCardRow title={t`Zip:`} value={contractor.zip} />
          <InfoCardRow title={t`Country:`} value={contractor.country?.name} />
        </Box>

        <Box p="6">
          <WrapComponent
            if={can("contract_update") && !isMobile}
            with={(children) => (
              <EditModalWrapper
                editStore={editStore}
                loadingUpdateContractor={loadingUpdateContractor}
                onEditContractor={onEditContractor}
                onOpenModal={onOpenModal}
              >
                {children}
              </EditModalWrapper>
            )}
          >
            <CardHeader title={t`Billing info`} icon={<CardIcon />} />
          </WrapComponent>

          <InfoCardRow
            title={t`Business form:`}
            value={contractor.businessFormReadable}
          />
          <InfoCardRow title={t`Reg. no:`} value={contractor.registration_no} />
          <InfoCardRow title={t`Tax ID:`} value={contractor.tax_no} />
          <InfoCardRow title={t`VAT no:`} value={contractor.vat_no} />
          <InfoCardRow title={t`National ID:`} value={contractor.national_id} />
          <InfoCardRow
            title={t`IBAN:`}
            value={contractor.iban ? formatIBAN(contractor.iban) : ""}
          />
          {workspaceStore.settings?.enableContractorFullBankAccountData && (
            <>
              <InfoCardRow
                title={t`Bank account:`}
                value={contractor.bank_account}
              />
              <InfoCardRow
                title={t`Bank code:`}
                value={contractor.bank_account_code}
              />
            </>
          )}
          <InfoCardRow title={t`SWIFT:`} value={contractor.swift} />
          <InfoCardRow
            title={t`VAT payer:`}
            value={contractor.isVatPayerReadable}
          />
        </Box>
      </Grid>

      <Box
        borderTop="1px"
        borderTopColor="gray.200"
        borderBottom="1px"
        borderBottomColor="gray.200"
      >
        <DocumentsCtx.Provider value={documentsStore}>
          <TableModuleDocuments bordered={false} />
        </DocumentsCtx.Provider>
      </Box>

      <Box pt="1">
        <TableModuleExpenses contractorId={contractor.id} bordered={false} />
      </Box>
    </div>
  );
});

type EditModalWrapperProps = {
  editStore: Store;
  onOpenModal: () => void;
  onEditContractor: () => void;
  loadingUpdateContractor: boolean;
};
const EditModalWrapper = observer(function EditModalWrapper({
  children,
  onOpenModal,
  editStore,
  onEditContractor,
  loadingUpdateContractor,
}: PropsWithChildren<EditModalWrapperProps>) {
  return (
    <HStack alignItems="start" justifyContent="space-between">
      {children}
      <ModalContractor.StoreContext.Provider value={editStore}>
        <React.Fragment>
          <Button
            colorScheme="grey"
            leftIcon={<Icon name="pencil-01" color="grey" />}
            onClick={onOpenModal}
            variant="outline"
          >
            <Trans>Edit details</Trans>
          </Button>
          <ModalContractor.Modal
            title={t`Edit contractor`}
            primaryAction={{
              onAction: onEditContractor,
              loading: loadingUpdateContractor,
            }}
          />
        </React.Fragment>
      </ModalContractor.StoreContext.Provider>
    </HStack>
  );
});
